import { appConfig } from '../app-config';
import type { CustomerModel } from '../types/models/customer-model';
import { getLocale } from '../locales/i18n';

/**
 * Session handling, replacing SuperTokens.
 *
 * SuperTokens kept the session in an httpOnly cookie and shipped ~200 kB of
 * prebuilt sign-in UI. We hold a JWT in localStorage instead, which is what
 * makes the WebSocket work at all: a browser cannot attach an Authorization
 * header to a WebSocket upgrade, so the token has to be readable by JS to be
 * put in the query string.
 *
 * The tradeoff is XSS reach. It is accepted here because the old design had a
 * worse problem in the same place — the server DECODED that cookie without
 * verifying its signature, so anyone could forge a session for any customer
 * without needing XSS at all.
 */

const TOKEN_KEY = 'niq.token';
const CUSTOMER_KEY = 'niq.customer';

export interface Session {
  token: string;
  customer: CustomerModel;
}

type Listener = (session: Session | null) => void;

const listeners = new Set<Listener>();

function read(): Session | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const raw = localStorage.getItem(CUSTOMER_KEY);
    if (!token || !raw) return null;
    return { token, customer: JSON.parse(raw) as CustomerModel };
  } catch {
    // A quota-exceeded or disabled-storage browser must land on the sign-in
    // screen, not a white page.
    return null;
  }
}

let current: Session | null = read();

function publish(): void {
  for (const listener of listeners) listener(current);
}

export const authStore = {
  get(): Session | null {
    return current;
  },

  set(session: Session): void {
    current = session;
    try {
      localStorage.setItem(TOKEN_KEY, session.token);
      localStorage.setItem(CUSTOMER_KEY, JSON.stringify(session.customer));
    } catch {
      // Session still works for this tab even if it cannot be persisted.
    }
    publish();
  },

  clear(): void {
    current = null;
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(CUSTOMER_KEY);
    } catch {
      /* nothing to clean up */
    }
    publish();
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }
};

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${appConfig.apiUrl}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body)
  });

  const payload = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) {
    throw new AuthError(payload.error ?? 'unknownError', response.status);
  }
  return payload;
}

export class AuthError extends Error {
  constructor(
    readonly code: string,
    readonly status: number
  ) {
    super(code);
    this.name = 'AuthError';
  }
}

export interface SignInResponse {
  token: string;
  customer: CustomerModel;
}

/**
 * Ask for a sign-in email. Always resolves for a well-formed address, whether
 * or not it is known — the server deliberately does not say, so the app must
 * not imply it either.
 */
export async function requestSignIn(email: string): Promise<{ expiresInSec: number }> {
  // The server writes the email in the app's language.
  return post('/api/mobile/v1/auth/request', { email, locale: getLocale() });
}

export async function verifyCode(email: string, code: string): Promise<Session> {
  const session = await post<SignInResponse>('/api/mobile/v1/auth/verify-code', { email, code });
  authStore.set(session);
  return session;
}

export async function verifyMagicLink(token: string): Promise<Session> {
  const session = await post<SignInResponse>('/api/mobile/v1/auth/verify-link', { token });
  authStore.set(session);
  return session;
}
